import { useHead } from "@unhead/vue";
import type { MetaEntry, SeoSpecificData } from '../../types/seo';

type UseMetaParams = {
    robots?: Ref<string>;
    title?: Ref<string>;
    meta?: Ref<MetaEntry[]>;
    canonicalUrl?: Ref<string | undefined>;
}

export function useMeta(params?: UseMetaParams) {
    const { data: seoSpecificData } = useNuxtData<SeoSpecificData>('seoSpecificData');
    // TODO: Remove when we implement SEO in Pond
    const { isRobotsNoIndex } = useRobotsPathConfig();
    const robotsValue: ComputedRef<string> = computed(() => {
        const hasIndexingFalse = seoSpecificData.value && !seoSpecificData.value.indexing;

        if (hasIndexingFalse) {
            return 'noindex,nofollow';
        }

        if (params?.robots?.value) {
            return params.robots.value;
        }

        if (isRobotsNoIndex.value) {
            return 'noindex,follow';
        }

        return 'index,follow';
    });

    const robotsMeta: ComputedRef<MetaEntry> = computed(() => {
        return { name: 'robots', content: robotsValue.value };
    });

    const ogMetaAllowedKeys = ['title', 'description'];
    const ogMeta: ComputedRef<MetaEntry[]> = computed(() =>
        params?.meta?.value
            .filter((meta) => ogMetaAllowedKeys.includes(meta.name))
            .map((meta) => ({
                name: `og:${meta.name}`,
                content: meta.content,
            })) ?? [],
    );

    const ogSiteName: ComputedRef<MetaEntry | undefined> = computed(() => {
        return params?.title
            ? { name: 'og:site_name', content: params?.title.value }
            : undefined;
    });

    const ogType = {
        name: 'og:type',
        content: 'website',
    };

    const meta: ComputedRef<MetaEntry[]> = computed(() => {
        const tags: MetaEntry[] = [];

        if (robotsMeta.value) {
            tags.push(robotsMeta.value);
        }

        if (params?.meta) {
            tags.push(...params.meta.value);
        }

        tags.push(...ogMeta.value);

        if (ogSiteName.value) {
            tags.push(ogSiteName.value);
        }

        tags.push(ogType);

        return tags;
    });

    const links = computed(() => {
        const links = [];

        if (params?.canonicalUrl?.value) {
            links.push({
                rel: 'canonical',
                href: params.canonicalUrl.value,
            });
        }

        return links;
    });

    useHead({
        title: params?.title,
        meta: meta,
        link: links,
    });
}
